import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { type Frame } from '@bizzdesign/sdk-population';
import {
  type PopulationItem,
  PopulationService,
} from '@bizzdesign/data-access-populations';
import { ContentOpenContainerComponent } from '@bizzdesign/feature-content-open';
import { baseRoutes } from '@bizzdesign/shared/utils';
import {
  type Population,
  type ToastActionButton,
} from '@bizzdesign/shared/models';
import { MessageService } from 'primeng/api';
import {
  BehaviorSubject,
  debounce,
  map,
  type Observable,
  of,
  ReplaySubject,
  scan,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { PopulationsOverviewComponent } from '../../components/populations-overview/populations-overview.component';
import { DeletePopulationModalComponent } from '../../components/delete-population-modal/delete-population-modal.component';
import { POPULATIONS_OVERVIEW_ROUTE } from '../../routes';
import { assertIsUUID } from '@bizzdesign/bizzdesign-sdk/uuid';

@Component({
  selector: 'bd-populations-container',
  templateUrl: './populations-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './populations-container.component.scss',
  imports: [
    PopulationsOverviewComponent,
    DeletePopulationModalComponent,
    ContentOpenContainerComponent,
    AsyncPipe,
  ],
})
export class PopulationsContainerComponent {
  private readonly populationService = inject(PopulationService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  addPopulationToFrameModalVisible = false;
  deletePopulationModalVisible = false;
  populationLoading = false;
  framesLoading = false;

  numberOfPopulationElements: number;

  populationNameSearchTerm$: BehaviorSubject<string | undefined> =
    new BehaviorSubject<string | undefined>('');
  isLoading$ = new BehaviorSubject(false);
  framesLoading$ = new BehaviorSubject(false);
  restorePopulation$ = new BehaviorSubject(false);
  usedInFrames$: Observable<Frame[]>;
  populationToBeDeleted: PopulationItem;
  deletedPopulationIndex: number;

  populations$: Observable<PopulationItem[]> =
    this.populationNameSearchTerm$.pipe(
      tap(() => this.isLoading$.next(true)),
      debounce((searchTerm) => (searchTerm !== '' ? timer(200) : of({}))),

      switchMap((searchTerm) =>
        this.populationService
          .getPopulations({
            searchTerm,
          })
          .pipe(
            scan((populations, pageResults) => {
              populations.push(...pageResults);
              this.isLoading$.next(false);
              return [...populations];
            }, new Array<PopulationItem>()),
          ),
      ),
    );

  populationItemSubject$: ReplaySubject<PopulationItem> = new ReplaySubject(1);

  population$: Observable<Population> = this.populationItemSubject$.pipe(
    tap(() => {
      this.addPopulationToFrameModalVisible = true;
      this.populationLoading = true;
    }),
    switchMap((populationItem) =>
      this.populationService.getDataForPopulation(populationItem.getData).pipe(
        tap((data) => {
          this.numberOfPopulationElements = data.length;
          this.populationLoading = false;
        }),

        map((data) => {
          assertIsUUID(populationItem.createdBy.userId);
          const result = {
            objects: data,
            relations: [],
            ...populationItem,
            createdBy: {
              ...populationItem.createdBy,
              userId: populationItem.createdBy.userId,
            },
          };
          delete result.getData;
          return result;
        }),
      ),
    ),
  );

  loadNextPage() {
    this.populationService.getNextPopulationPage();
  }

  onOpenPopulationInFrameModalVisibleChange(visible: boolean) {
    this.addPopulationToFrameModalVisible = visible;
  }

  onOpenPopulationInFrame(populationItem: PopulationItem) {
    this.addPopulationToFrameModalVisible = true;
    this.populationLoading = true;
    this.populationItemSubject$.next(populationItem);
  }

  onOpenDeletePopulation(populationItem: PopulationItem) {
    this.framesLoading$.next(true);
    this.deletePopulationModalVisible = true;
    this.populationToBeDeleted = populationItem;
    this.usedInFrames$ = this.populationService
      .getPopulation(populationItem.id, true)
      .pipe(
        scan((frames, populationEntity) => {
          if (populationEntity !== null && populationEntity !== undefined) {
            frames.push(...populationEntity.frames);
          }
          this.framesLoading$.next(false);
          return [...frames];
        }, new Array<Frame>()),
      );
  }

  onEditDataForObjectsInPopulation(populationItem: PopulationItem) {
    this.router.navigate([baseRoutes.manageData, populationItem.id]);
  }

  onVisibleChange(visible: boolean) {
    this.deletePopulationModalVisible = visible;
  }

  onDeletePopulation(populationToDelete: PopulationItem) {
    this.isLoading$.next(true);
    this.populations$ = this.populations$.pipe(
      map((populations) => {
        this.deletedPopulationIndex = populations.findIndex(
          (population) => population.id === populationToDelete.id,
        );
        this.isLoading$.next(false);
        return populations.filter(
          (population) => population.id !== populationToDelete.id,
        );
      }),
    );
    this.restorePopulation$.next(false);
    const messageLife = 3000;
    const toastAction: ToastActionButton = {
      labelKey: 'deletePopulationModal.undoButtonTitle',
      tScope: 'featurePopulations',
      onClick: () => {
        this.messageService.clear();
        this.messageService.add({
          severity: 'success',
          summary: 'deletePopulationModal.restorationConfirmationMessage',
          data: {
            summaryParams: { populationName: populationToDelete.name },
            tScope: 'featurePopulations',
          },
        });
        this.restorePopulation$.next(true);
        this.isLoading$.next(true);
        this.populations$ = this.populations$.pipe(
          map((populations) => {
            populations.splice(
              this.deletedPopulationIndex,
              0,
              populationToDelete,
            );
            this.isLoading$.next(false);
            return [...populations];
          }),
        );
      },
    };
    this.messageService.add({
      severity: 'success',
      summary: 'deletePopulationModal.deletionConfirmationMessage',
      life: messageLife,
      data: {
        summaryParams: { populationName: populationToDelete.name },
        toastAction,
        tScope: 'featurePopulations',
      },
    });
    setTimeout(() => {
      if (!this.restorePopulation$.value) {
        this.populationService.deletePopulation(populationToDelete.id);
      }
    }, messageLife);
  }

  searchPopulations(populationNameSearchTerm: string) {
    this.populationNameSearchTerm$.next(populationNameSearchTerm);
  }

  navigateToSearch() {
    this.router.navigate([baseRoutes.home, 'search'], {
      state: { originate: POPULATIONS_OVERVIEW_ROUTE },
    });
  }
}
